/**
 * Swiper initialization for custom carousel
 * Handles Shopify Theme Editor events and safe configuration parsing
 */

// Type definitions for better TypeScript support
/** @typedef {Object} SwiperConfig
 * @property {number} [slidesPerView]
 * @property {number} [spaceBetween]
 * @property {number} [speed]
 * @property {boolean} [loop]
 * @property {string} [direction]
 * @property {{ rows: number }} [grid]
 * @property {boolean|{ delay: number, disableOnInteraction: boolean }} [autoplay]
 * @property {Object} [breakpoints]
 * @property {{ prevEl?: Element, nextEl?: Element }} [navigation]
 * @property {{ el?: Element, type?: string, clickable?: boolean }} [pagination]
 */

/** @typedef {Element & { swiper?: any, dataset?: DOMStringMap }} ElementWithSwiper */

/** @typedef {Window & { Swiper?: any }} WindowWithSwiper */

function initSwipers(scope = document) {
  // Select all elements with [e-swiper-slider] inside scope
  const swiperElements = scope.querySelectorAll('[e-swiper-slider]');
  
  swiperElements.forEach((el) => {
    /** @type {ElementWithSwiper} */
    const swiperEl = el;
    
    // Prevent duplicate initialization
    if (swiperEl.swiper) {
      return;
    }
    
    // Parse JSON safely from data-config attribute
    /** @type {any} */
    let config = {};
    try {
      const configString = swiperEl.dataset?.config || 'slidesPerView:1, spaceBetween:10, loop:true';
      
      // Handle both JSON format and simplified format
      if (configString.trim().startsWith('{')) {
        config = JSON.parse(configString);
      } else {
        // Parse simplified format: "slidesPerView:1, spaceBetween:10, loop:true"
        config = {};
        configString.split(',').forEach(pair => {
          const [key, value] = pair.trim().split(':');
          if (key && value) {
            const trimmedKey = key.trim();
            const trimmedValue = value.trim();
            
            // Convert string values to appropriate types
            if (trimmedValue === 'true') config[trimmedKey] = true;
            else if (trimmedValue === 'false') config[trimmedKey] = false;
            else if (!isNaN(Number(trimmedValue))) config[trimmedKey] = Number(trimmedValue);
            else config[trimmedKey] = trimmedValue;
          }
        });
      }
    } catch (error) {
      console.warn('Invalid Swiper configuration, using defaults');
      config = { slidesPerView: 1, spaceBetween: 10, loop: true };
    }
    
    // Build Swiper configuration
    /** @type {SwiperConfig} */
    const swiperConfig = {
      slidesPerView: config.slidesPerView || 1,
      spaceBetween: config.gap || 0,
      speed: config.speed || 300,
      loop: config.loop || false,
      direction: config.direction || 'horizontal',
      grid: config.rows ? { rows: config.rows } : undefined,
      autoplay: config.autoplay && config.autoplay.enabled ? {
        delay: config.autoplay.delay || 3000,
        disableOnInteraction: false
      } : false,
      breakpoints: config.breakpoints || {}
    };
    
    // Navigation logic
    if (config.navigation && config.navigation.enabled) {
      /** @type {{ prevEl?: Element, nextEl?: Element }} */
      const navigation = {};
      
      if (config.navigation.prevEl) {
        const prevEl = resolveSelector(config.navigation.prevEl, scope);
        if (prevEl) navigation.prevEl = prevEl;
      } else {
        const prevEl = swiperEl.querySelector('.swiper-button-prev');
        if (prevEl) navigation.prevEl = prevEl;
      }
      
      if (config.navigation.nextEl) {
        const nextEl = resolveSelector(config.navigation.nextEl, scope);
        if (nextEl) navigation.nextEl = nextEl;
      } else {
        const nextEl = swiperEl.querySelector('.swiper-button-next');
        if (nextEl) navigation.nextEl = nextEl;
      }
      
      if (navigation.prevEl && navigation.nextEl) {
        swiperConfig.navigation = navigation;
      }
    }
    
    // Pagination logic
    if (config.pagination && config.pagination.enabled) {
      /** @type {{ el?: Element, type?: string, clickable?: boolean }} */
      const pagination = {};
      
      if (config.pagination.el) {
        const paginationEl = resolveSelector(config.pagination.el, scope);
        if (paginationEl) pagination.el = paginationEl;
      } else {
        const paginationEl = swiperEl.querySelector('.swiper-pagination');
        if (paginationEl) pagination.el = paginationEl;
      }
      
      if (pagination.el) {
        pagination.type = config.pagination.type || 'bullets';
        pagination.clickable = true;
        swiperConfig.pagination = pagination;
      }
    }
    
    // Initialize Swiper
    /** @type {WindowWithSwiper} */
    const windowWithSwiper = window;
    if (windowWithSwiper.Swiper) {
      swiperEl.swiper = new windowWithSwiper.Swiper(swiperEl, swiperConfig);
    } else {
      console.warn('Swiper library not loaded');
    }
  });
}

/**
 * Resolve selector string to DOM element
 * First tries scope.querySelector, then document.querySelector
 * @param {string} selector
 * @param {Document|Element} scope
 * @returns {Element|null}
 */
function resolveSelector(selector, scope) {
  if (typeof selector !== 'string') return null;
  
  try {
    // Try scope first
    let element = scope.querySelector(selector);
    if (element) return element;
    
    // Fallback to document
    element = document.querySelector(selector);
    return element;
  } catch (error) {
    return null;
  }
}

// Theme Editor support
document.addEventListener("shopify:section:load", /** @param {Event} e */ e => {
  if (e.target) {
    initSwipers(/** @type {Document} */ (e.target));
  }
});

document.addEventListener("shopify:section:unload", /** @param {Event} e */ e => {
  if (e.target && 'querySelectorAll' in e.target) {
    const target = /** @type {ElementWithSwiper} */ (e.target);
    if ('querySelectorAll' in target) {
      target.querySelectorAll('[e-swiper-slider]').forEach((el) => {
        /** @type {ElementWithSwiper} */
        const swiperEl = el;
        if (swiperEl.swiper) {
          swiperEl.swiper.destroy(true, true);
          swiperEl.swiper = null;
        }
      });
    }
  }
});

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  initSwipers();
});

// Export for potential external use
window.initSwipers = initSwipers;
